import { LitElement, html, css, ifDefined, classMap } from '@hh/global.js'

class HHCustomSelect extends LitElement {
  static properties = {
    options: {
      type: Object,
    },
    name: {
      type: String,
    },
    active: {
      type: String,
    },
    lang: {
      type: Boolean,
    },
    position: {
      type: String,
    },
    _selected: {
      state: true,
    },
    _active: {
      state: true,
    },
    mouseover: {
      reflect: true,
    },
  }

  constructor() {
    super()
    this.handleBodyClick = this.handleBodyClick.bind(this)
    this.uuid = Math.random() * (1000000 - 1) + 1
    this.position = 'bottom'
  }

  connectedCallback() {
    super.connectedCallback()
    this._selected = this.active
    document.body.addEventListener('click', this.handleBodyClick)
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    document.body.addEventListener('click', this.handleBodyClick)
  }

  handleBodyClick(e) {
    const path = e.composedPath()
    if (
      path.some((node) => {
        return node === this
      })
    ) {
      return true
    }

    this._active && this.toggle()
  }

  toggle(e = false) {
    if (e && e.type === 'keyup' && !~['ESCAPE'].indexOf(e.code.toUpperCase())) {
      return true
    }

    this._active = !this._active
  }

  onMouseOver(e) {
    this.mouseover = true
  }

  onMouseLeave(e) {
    this.mouseover = false
  }

  selectValue(e, value) {
    e.preventDefault()
    this._selected = value
    ;(this.querySelector('[slot="input"]') || {}).value = value
    this.dispatchEvent(
      new CustomEvent('change', {
        bubbles: true,
        composed: true,
        detail: {
          value,
        },
      })
    )
    this.toggle()
  }

  get selectedLabel() {
    const option = this.options.find(
      (option) => option.value === this._selected
    )
    if (!option) return 'Invalid selected option'
    return option.label
  }

  sanitize(value) {
    return value.replace('&amp;', '&')
  }

  render() {
    return html`<div>
      <div class="container">
        <div id="${this.uuid}-label">
          <slot name="title"></slot>
        </div>
        <button
          @click=${this.toggle}
          @mouseover="${this.onMouseOver}"
          @mouseleave="${this.onMouseLeave}"
          type="button"
          class="button"
          aria-expanded="${this._active}"
          aria-controls="${this.uuid}-list"
          aria-describedby="${this.uuid}-label"
        >
          <span class="button__label">${this.selectedLabel}</span>
          <slot name="icon"></slot>
        </button>
        <slot name="input"></slot>
        <hh-focus-trap active="${this._active}">
          <ul
            id="${this.uuid}-list"
            class="${classMap({
              list: true,
              'is-active': this._active,
              'position-top': this.position === 'top',
            })}"
            @keyup="${this.toggle}"
            aria-hidden="${!this._active}"
            role="list"
          >
            ${this.options.map((option) => {
              this.lang && (option.lang = option.value)
              return html`<li
                class="${classMap({
                  option: true,
                  'is-active': option.value === this._selected,
                })}"
              >
                <a
                  @click="${(e) => this.selectValue(e, option.value)}"
                  class="link"
                  href="#"
                  hreflang="${ifDefined(option.lang)}"
                  lang="${ifDefined(option.lang)}"
                >
                  ${this.sanitize(option.label)}
                </a>
              </li>`
            })}
          </ul>
        </hh-focus-trap>
      </div>
    </div>`
  }

  static styles = css`
    :host {
      width: 100%;
    }

    :host button {
      transition: all 0.3s;
    }

    :host([mouseover='true']) button {
      border-color: var(--hh-color-brand-tertiary);
      background-color: var(--hh-color-reverse);
      box-shadow: 0 0 2rem 0 rgba(0, 0, 0, 0.15);
    }

    :host([disabled]) {
      pointer-events: none;
      color: var(--hh-color-neutral);
    }

    :host([disabled]) ::slotted([slot='title']) {
      color: var(--hh-color-neutral);
    }

    :host([disabled]) button {
      color: var(--hh-color-neutral);
    }

    :host([error]) button {
      border-color: var(--hh-color-danger);
    }

    .container {
      position: relative;
    }

    .button {
      align-items: center;
      background: transparent;
      border: solid var(--hh-color-brand-secondary);
      border-width: 0 0 var(--hh-border-width, 1px);
      cursor: pointer;
      display: flex;
      font: var(--hh-body-font);
      height: var(--hh-height, 35px);
      justify-content: space-between;
      padding: 0 5px;
      width: 100%;
    }

    .button:focus-visible {
      outline: 0.2rem solid var(--hh-color-focus);
      outline-offset: 0;
    }

    .button__label {
      position: relative;
      top: 8px;
    }

    ::slotted([slot='button']),
    ::slotted([slot='icon']) {
      align-items: center;
      display: flex;
      height: 100%;
    }

    ::slotted([slot='icon']) {
      width: 14px;
      margin: -4px 10px 0 30px;
    }

    ::slotted([slot='title']) {
      color: var(--hh-color-subdued);
      left: 5px;
      margin: 0;
      position: absolute;
      top: 4px;
      pointer-events: none;
    }

    svg {
      width: 100%;
    }

    .list {
      background-color: var(--hh-background, white);
      border: var(--hh-border-width, 1px) solid currentColor;
      display: block;
      margin: 0;
      max-height: var(--hh-options-max-height, 19rem);
      max-width: var(--hh-options-max-width, 100%);
      min-height: var(--hh-options-min-height, 8.2rem);
      min-width: var(--hh-options-min-width, 12rem);
      opacity: 0;
      overflow-y: auto;
      padding-left: 0;
      position: absolute;
      transform: translateY(0);
      transition: visibility var(--hh-duration-default, 300ms)
          var(--hh-duration-default, 300ms),
        opacity var(--hh-duration-default, 300ms),
        transform var(--hh-duration-default, 300ms);
      visibility: hidden;
      width: 100%;
      z-index: 1;
    }

    .list.position-top {
      bottom: 100%;
    }

    .list.is-active {
      opacity: 1;
      transform: translateY(5px);
      transition: transform var(--hh-duration-default, 300ms) 1ms,
        opacity var(--hh-duration-default, 300ms) 1ms, visibility 1ms;
      visibility: visible;
    }

    .option {
      list-style: none;
    }

    .option.is-active {
      opacity: 0.5;
    }

    .link {
      color: inherit;
      display: block;
      padding: var(--hh-link-padding, 1em);
      text-decoration: none;
    }
  `
}

customElements.define('hh-custom-select', HHCustomSelect)
